import { Button, cn } from '@merels/ui'
import type { DynamicGameCatalogEntry } from '../../../../deps/catalog/dynamicGameLoader/dynamicGameLoader.types'

interface GamePublishInfoProps {
  gameId: string | null
  channel: string | null
  hasActiveGame: boolean
  buildInfo: DynamicGameCatalogEntry | null
  isDynamicallyLoaded: boolean
  /** Sticky per-game channel preference (see @merels/room's setGameChannelPreference), or null if unset. */
  channelPreference: string | null
  onSetChannelPreference: (channel: string) => void
  /** Every published version for this game, most recent first. */
  availableBuilds: DynamicGameCatalogEntry[]
  onSelectBuild: (version: import('@merels/room').ResolvedGameVersion) => void
  onRefreshBuilds: () => void
  /** versionIds whose full publish message is currently expanded. */
  expandedBuildMessageIds: Set<string>
  onToggleBuildMessageExpanded: (versionId: string) => void
}

// Splits a git-commit-style message into its summary (first line) and body
// (everything after, blank-line-separated or not — trimmed either way).
function splitMessage(message: string): { summary: string; body: string } {
  const newlineIndex = message.indexOf('\n')
  if (newlineIndex === -1) return { summary: message, body: '' }
  return { summary: message.slice(0, newlineIndex), body: message.slice(newlineIndex + 1).trim() }
}

function BuildMessage({
  message,
  isExpanded,
  onToggle,
}: {
  message: string
  isExpanded: boolean
  onToggle: () => void
}) {
  const { summary, body } = splitMessage(message)
  return (
    <div className="text-xs mt-1">
      <button
        type="button"
        className={cn('text-left text-foreground/80', body && 'hover:text-foreground hover:underline')}
        onClick={body ? onToggle : undefined}
      >
        {summary}
        {body && <span className="text-muted ml-1">{isExpanded ? '(less)' : '(more)'}</span>}
      </button>
      {isExpanded && body && <div className="whitespace-pre-wrap text-muted mt-0.5">{body}</div>}
    </div>
  )
}

// Non-"stable" channels are the only ones a room subscribes to for live
// pushes (see @merels/room's DEFAULT_GAME_CHANNEL / GameCatalogClient) — so
// "stable" always means this game's version is pinned for the rest of the
// game, and anything else means the room is live-following that channel.
function describeMode(channel: string): 'Pinned' | 'Following' {
  return channel === 'stable' ? 'Pinned' : 'Following'
}

export function GamePublishInfo({
  gameId,
  channel,
  hasActiveGame,
  buildInfo,
  isDynamicallyLoaded,
  channelPreference,
  onSetChannelPreference,
  availableBuilds,
  onSelectBuild,
  onRefreshBuilds,
  expandedBuildMessageIds,
  onToggleBuildMessageExpanded,
}: GamePublishInfoProps) {
  const effectivePreference = channelPreference ?? 'stable'
  return (
    <div className="p-4 space-y-4 border-t border-border">
      <div className="text-sm font-semibold">Game Publishing</div>

      <div className="space-y-2">
        <div className="text-sm font-semibold">Channel</div>
        <div className="text-sm flex items-center gap-2">
          {channel ? (
            <span className="inline-block px-2 py-1 rounded bg-accent/10 text-accent text-xs font-medium">
              {channel}
            </span>
          ) : (
            <span className="text-muted">Not following a channel</span>
          )}
          {channel && hasActiveGame && (
            <span className="inline-block px-2 py-1 rounded bg-muted/10 text-muted text-xs font-medium">
              {describeMode(channel)}
            </span>
          )}
        </div>
        <div className="text-[10px] text-muted/60">
          {hasActiveGame
            ? describeMode(channel ?? 'stable') === 'Pinned'
              ? 'Pinned: this game keeps this exact version for the rest of the game, even if a new one publishes.'
              : 'Following: this room live-updates to whatever is newest on this channel as it publishes.'
            : 'No game active — this is the session default channel used to resolve one at start.'}
        </div>
      </div>

      {gameId && (
        <div className="space-y-2">
          <div className="text-sm font-semibold">Next Game Starts On</div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={effectivePreference === 'stable' ? 'default' : 'outline'}
              onClick={() => onSetChannelPreference('stable')}
            >
              Stable
            </Button>
            <Button
              size="sm"
              variant={effectivePreference === 'preview' ? 'default' : 'outline'}
              onClick={() => onSetChannelPreference('preview')}
            >
              Preview
            </Button>
          </div>
          <div className="text-[10px] text-muted/60">
            Sticky for "{gameId}" in this room — applies the next time this game starts (rematch, restart, etc.),
            not to the game in progress right now.
          </div>
        </div>
      )}

      <div className="space-y-2">
        <div className="text-sm font-semibold">Active Game Build</div>

        {!gameId && <div className="text-sm text-muted">No active game</div>}

        {gameId && !isDynamicallyLoaded && (
          <div className="text-sm text-muted">
            "{gameId}" is statically bundled, not dynamically loaded
          </div>
        )}

        {gameId && isDynamicallyLoaded && !buildInfo && (
          <div className="text-sm text-muted">Loading build info…</div>
        )}

        {gameId && isDynamicallyLoaded && buildInfo && (
          <div className="text-sm space-y-1">
            <div>
              <span className="text-muted">Version: </span>
              <span className="font-mono">{buildInfo.versionId}</span>
            </div>
            {buildInfo.publishedAt && (
              <div>
                <span className="text-muted">Published: </span>
                {new Date(buildInfo.publishedAt).toLocaleString()}
              </div>
            )}
            <div className="break-all">
              <span className="text-muted">Bundle: </span>
              <a
                href={buildInfo.clientBundleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:underline font-mono"
              >
                {buildInfo.clientBundleUrl}
              </a>
            </div>
          </div>
        )}
      </div>

      {gameId && isDynamicallyLoaded && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold">Available Builds</div>
            <Button size="sm" variant="ghost" onClick={onRefreshBuilds}>
              Refresh
            </Button>
          </div>

          {availableBuilds.length === 0 && (
            <div className="text-sm text-muted">No published builds found for "{gameId}"</div>
          )}

          {availableBuilds.length > 0 && (
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {availableBuilds.map((build) => {
                const isActive = buildInfo?.versionId === build.versionId
                return (
                  <div
                    key={build.versionId}
                    className="flex items-start justify-between gap-2 p-2 rounded border border-border text-sm"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-xs truncate">{build.versionId}</span>
                        {build.channels?.map((c) => (
                          <span
                            key={c}
                            className="inline-block px-1.5 py-0.5 rounded bg-accent/10 text-accent text-[10px] font-medium"
                          >
                            {c}
                          </span>
                        ))}
                      </div>
                      {build.publishedAt && (
                        <div className="text-[10px] text-muted/60">
                          {new Date(build.publishedAt).toLocaleString()}
                          {build.creator && (
                            <span className="ml-1 text-muted">
                              by {build.creator.displayName}
                              {build.creator.creatorType !== 'human' && (
                                <span className="ml-1 text-[8px] uppercase font-bold text-accent px-1 py-0.5 rounded bg-accent/10">
                                  {build.creator.creatorType}
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                      )}
                      {build.message && (
                        <BuildMessage
                          message={build.message}
                          isExpanded={expandedBuildMessageIds.has(build.versionId)}
                          onToggle={() => onToggleBuildMessageExpanded(build.versionId)}
                        />
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant={isActive ? 'default' : 'outline'}
                      disabled={isActive}
                      onClick={() =>
                        onSelectBuild({
                          versionId: build.versionId,
                          clientBundleUrl: build.clientBundleUrl,
                          styleUrl: build.styleUrl,
                          metadata: {
                            minPlayers: build.minPlayers ?? 2,
                            maxPlayers: build.maxPlayers ?? build.minPlayers ?? 2,
                            displayName: build.displayName,
                            description: build.description,
                            supportsSharedScreen: build.supportsSharedScreen,
                            options: build.options,
                            playerIds: build.playerIds,
                          },
                        })
                      }
                    >
                      {isActive ? 'Active' : 'Use'}
                    </Button>
                  </div>
                )
              })}
            </div>
          )}
          <div className="text-[10px] text-muted/60">
            Previews this exact build for the game in progress right now. Doesn't change the channel or the
            sticky preference above — a later channel publish can still override it.
          </div>
        </div>
      )}
    </div>
  )
}
